import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';

import { Button, ExternalLinkButton } from '@/components/base/buttons/button';
import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { notify } from '@/components/base/notifications/notifications';
import { useCompleteProject } from '@/hooks/api/use-complete-project';
import { ERROR_CAUSES } from '@/utils/constants';
import { projectDetailHref } from '@/utils/project-task-complete-storage';
import { completeExternalStudy } from '@/utils/session/complete-external-study';
import { getPublicEnv } from '@/utils/session/get-public-env';

import { requireParticipantSession } from '../-utils/participant-route-guards';

const completeSearchSchema = z.object({
  cc: z.string(),
});

/**
 * Where a researcher's survey redirects a participant when they finish,
 * with the project's completion id (`?cc=`). One URL for every participant
 * on the project, so this page decides who is completing:
 *
 * - an external participant is signed in per project, never into the
 *   participant app, so their completion is tried first with the browser's
 *   external sessions, whether or not someone is also signed into the
 *   participant app: researchers invite people who already use Respondent,
 *   and core-api scopes each try to that session's account and the
 *   completion id, so only the external response that owns the id can be
 *   completed this way;
 * - otherwise a signed-in marketplace participant completes through their
 *   own account, as the page always has, and nobody is sent to login.
 *
 * Deliberately outside the authenticated layout: its onboarding and
 * verification gates would bounce a participant before their completion
 * was recorded, and the completion is the one thing this page is for.
 */
export const Route = createFileRoute('/participants/_public/complete')({
  validateSearch: completeSearchSchema,
  beforeLoad: async ({ context, location, search }) => {
    const { user } = context;

    const completion = await completeExternalStudy({
      data: { projectCompletionId: search.cc },
    });
    if (completion === 'COMPLETED') {
      throw redirect({ to: '/participants/invite/done' });
    }
    if (completion === 'FAILED') {
      throw new Error(
        'We could not record your completion. Please try again, or contact support@respondent.io.',
      );
    }

    requireParticipantSession(user, await getPublicEnv(), location);

    return { user };
  },
  component: CompletePage,
});

function CompletePage() {
  const { user } = Route.useRouteContext();
  const { cc } = Route.useSearch();
  const { mutate: completeProject, error, isError } = useCompleteProject();
  const navigate = useNavigate();
  const completionAttemptKeyRef = useRef<string | null>(null);

  const isAlreadyCompletedError =
    error instanceof Error &&
    error.cause === ERROR_CAUSES.PROJECT_ALREADY_COMPLETED;

  const showFailureUI = !isAlreadyCompletedError && isError;

  useEffect(() => {
    const completionAttemptKey = `${cc}:${user.id}`;
    if (completionAttemptKeyRef.current === completionAttemptKey) {
      return;
    }

    completionAttemptKeyRef.current = completionAttemptKey;
    completeProject(
      {
        projectCompletionId: cc,
        userId: user.id,
      },
      {
        onSuccess: (data) => {
          // Hard navigation: avoid TanStack soft-nav / ensureQueryData races
          // after returning from an external survey in this same tab.
          if (data?.projectId) {
            globalThis.location.replace(projectDetailHref(data.projectId));
            return;
          }

          void navigate({
            to: '/participants/my-projects',
            replace: true,
            reloadDocument: true,
          });
        },
        onError: (error) => {
          if (error.cause === ERROR_CAUSES.PROJECT_ALREADY_COMPLETED) {
            notify({
              title: t`Project already completed`,
              description: t`You have already completed this project.`,
              color: 'success',
            });

            void navigate({
              to: '/participants/projects',
              replace: true,
              reloadDocument: true,
            });
            return;
          }

          notify({
            title: t`Failed to complete project`,
            description: error.message,
            color: 'error',
          });
        },
      },
    );
  }, [cc, user.id, completeProject, navigate]);

  return (
    <div className="px-auto py-auto flex min-h-[calc(100vh-4.5rem)] flex-1 flex-col items-center justify-center gap-6 text-center">
      {showFailureUI ? (
        <>
          <h1 className="text-display-xs font-semibold text-primary">
            <Trans>Failed to complete project</Trans>
          </h1>
          <div className="mt-6 flex flex-col items-center justify-center gap-6">
            <Button onClick={() => globalThis.location.reload()}>
              <Trans>Try again</Trans>
            </Button>
            <ExternalLinkButton
              color="link-color"
              href="mailto:support@respondent.io"
            >
              <Trans>Contact support</Trans>
            </ExternalLinkButton>
          </div>
        </>
      ) : (
        <>
          <LoadingIndicator type="line-simple" size="md" />
          <div className="max-w-lg">
            <h1 className="text-display-xs font-semibold text-primary">
              <Trans>Completing your survey</Trans>
            </h1>
            <p className="mt-4 text-md text-tertiary">
              <Trans>This may take a few seconds...</Trans>
            </p>
          </div>
        </>
      )}
    </div>
  );
}
